import React from 'react';
import { sortBy, castArray, cloneDeep, findIndex, xorBy, compact, get } from 'lodash';
import { CloseOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Divider, Tabs, Popconfirm, message, List, Space, Tooltip } from 'antd';
import {
  COMPARE_PATH_TEST_STR,
  USER_ROLE_READONLY,
  DASHBOARD_TYPE,
  MAX_COMPARE_FILTER_BARS,
  ALL_TRAFFIC,
  isAppModule,
} from '@utils/constants';
import { checkIfFilterManagementEnabled } from '@utils/filter';
import { validateNewFilterBar } from '@components/compare-drawer/utils/checks';
import featureNames from '@utils/featureToggle/constants';
import {
  getFilterFromRules,
  getRulesFromFilter,
  isDuplicateFilter,
  isValidSavedFilter,
} from '@utils';
import { FIlTER_CONTAINS_INAPPLICALBE_DIMENSION_ERROR , INAPPLICABLE_FILTER_ERROR } from '@components/summary-panel/FilterDropdown/FilterStatus';
import FilterPill from './FilterPill';
import SavedFilterPanel from './SavedFilterPanel';
import CreateFilterPanel from './NewFilterPanel';
import styles from './FilterContainer.less';
import {
  RECENT_FILTERS_LIMIT,
  FIXED_FILTERS,
  FIXED_APP_FILTERS,
  EXISTING_FILTER_PANEL_KEY,
  SAVE_FILTER_PANEL_KEY,
  MANAGE_FILTERS,
  DEFAULT_FILTERS,
  RECENT_FILTERS,
} from './constants';
import { defaultProps } from './propTypes';

import FilterSearch from './FilterSearch';
import { OPEN_FILTER_MANAGEMENT_EVENT } from '@components/filter/containers/FilterManagement';

const DERIVED_FIXED_FILTER = isAppModule ? FIXED_APP_FILTERS : FIXED_FILTERS;

const getFullFilters = (filters, defaultFilters, dashboardType) => {
  if (dashboardType === DASHBOARD_TYPE.NOC && isAppModule) {
    return filters.concat(defaultFilters);
  }
  return filters.concat(DERIVED_FIXED_FILTER);
};

export default class FilterContainer extends React.Component {
  componentDidMount() {
    const {
      updateActiveTab,
      appliedFilters,
      updateSelectedFilters,
      dashboardType,
      getFilters, // eslint-disable-line
    }: any = this.props;
    getFilters();

    if (dashboardType !== DASHBOARD_TYPE.COMPARE && dashboardType !== DASHBOARD_TYPE.NOC) {
      updateSelectedFilters(appliedFilters);
    }

    switch (dashboardType) {
      case DASHBOARD_TYPE.NOC:
      case DASHBOARD_TYPE.ML:
      case DASHBOARD_TYPE.COMPARE:
        // open saved filters sub tab for compare dashboard by default
        updateActiveTab('1');
        break;
      default:
        updateActiveTab('2');
        break;
    }

    if (isAppModule) {
      switch (dashboardType) {
        case DASHBOARD_TYPE.COMPARE:
        case DASHBOARD_TYPE.NOC:
          updateActiveTab('1');
          break;
        default:
          updateActiveTab('2');
      }
    }

    this.isSelectedFiltersUpdated = false;
    this.fillSelectedFilters();
  }

  componentDidUpdate(prevProps) {
    const { filters } = this.props;
    if (prevProps.filters !== filters) {
      this.fillSelectedFilters();
    }
  }

  renderTabBarExtraContent = () => {
    const { changeFilterContainerVisibility } = this.props;
    const isNewFilterManagmentEnabled = checkIfFilterManagementEnabled();

    // new filter management is enabled
    if (isNewFilterManagmentEnabled) {
      const handleOnClick = () => {
        const event = new CustomEvent(OPEN_FILTER_MANAGEMENT_EVENT);
        window.dispatchEvent(event);
        changeFilterContainerVisibility(false);
      };
      return (
        <span onClick={handleOnClick} className={styles.manageFiltersBtn}>
          {MANAGE_FILTERS}
        </span>
      );
    }

    // ECO don't have link to go to pulse4 filter management page which is only for Video/Ads.
    if (isAppModule) return null;

    const manageFilterLink = window.__POWERED_BY_QIANKUN__ ? (
      <a href="/app/pulse/filters/">{MANAGE_FILTERS}</a>
    ) : (
      <Popconfirm
        title="Do you want to exit new Pulse?"
        onConfirm={this.confirm}
        okText="Yes"
        cancelText="No"
      >
        <a href="/">{MANAGE_FILTERS}</a>
      </Popconfirm>
    );
    return manageFilterLink;
  };

  render() {
    const {
      activeTabKey,
      filters,
      type,
      selectedFilters,
      updateActiveTab,
      dashboardId,
      userRole,
      features,
      maxSelectedFilters,
      dashboardType,
      deleteFilter,
      startEditFilter,
      isAdmin,
      userProfile,
    }: any = this.props;

    const { [featureNames.CREATE_FILTER]: createFilter } = features;
    const isNewFilterManagmentEnabled = checkIfFilterManagementEnabled();
    const disableCreateFilter = !createFilter || userRole === USER_ROLE_READONLY;

    // eslint-disable-next-line max-len
    const showCreateFilter = ![DASHBOARD_TYPE.NOC, DASHBOARD_TYPE.ML].includes(dashboardType);

    const onConfirmDelete = (filter) => {
      deleteFilter(filter.id);
    };

    const handleEditFilter = (filter) => {
      const [category, subcategory] = (filter.category || '').split('/');
      startEditFilter({
        ...filter,
        category: category || '',
        subcategory: subcategory || '',
      });
      //  go to edit panel
      updateActiveTab('2');
    };

    return (
      <div className={styles.filterContainer}>
        <CloseOutlined className={styles.closeBtn} onClick={this.onCancel} />
        <FilterSearch
          filters={filters}
          selectedFilters={selectedFilters}
          onSelect={this.onSearchSelect}
        />
        {this.renderFilterPills()}
        <Divider style={{ margin: '1rem 0' }} />
        <Tabs
          type="card"
          size="Small"
          activeKey={activeTabKey}
          onChange={updateActiveTab}
          tabBarExtraContent={this.renderTabBarExtraContent()}
        >
          {showCreateFilter ? (
            <Tabs.TabPane
              key={SAVE_FILTER_PANEL_KEY}
              tab={type === 'create' ? 'Create New Filter' : 'Edit Filter'}
              disabled={disableCreateFilter}
            >
              <CreateFilterPanel dashboardId={dashboardId} onCancel={this.onCancel} />
            </Tabs.TabPane>
          ) : null}
          {
            <Tabs.TabPane tab="Saved Filters" key={EXISTING_FILTER_PANEL_KEY}>
              <SavedFilterPanel
                filters={filters}
                onCancel={this.onCancel}
                onCheck={this.updateSelectedFiltersLocal}
                onApply={this.applyFilters}
                selectedFilters={selectedFilters}
                allowMultiSelection={maxSelectedFilters > 1}
              />
            </Tabs.TabPane>
          }
          {isAppModule && !isNewFilterManagmentEnabled && showCreateFilter ? (
            <Tabs.TabPane tab="Manage Filters" key={'manage'}>
              <div style={{ maxHeight: 400, overflow: 'auto' }}>
                <List
                  bordered
                  dataSource={filters}
                  renderItem={(item: any) => {
                    const forbiddenUpdateFilter =
                      !isAdmin && item.createdBy !== userProfile.profile_id;
                    const showDeleteIcon =
                      selectedFilters.length && selectedFilters[0].fid !== item.id;

                    const deleteIcon = showDeleteIcon ? (
                      <Popconfirm
                        disabled={forbiddenUpdateFilter}
                        placement="topLeft"
                        title={'Are you sure to delete this filter?'}
                        onConfirm={() => {
                          onConfirmDelete(item);
                        }}
                        okText="Yes"
                        cancelText="No"
                      >
                        <DeleteOutlined
                          className={forbiddenUpdateFilter ? styles.disabledIcon : ''}
                        />
                      </Popconfirm>
                    ) : null;

                    const editIcon = (
                      <EditOutlined
                        className={forbiddenUpdateFilter ? styles.disabledIcon : ''}
                        onClick={forbiddenUpdateFilter ? undefined : () => handleEditFilter(item)}
                      />
                    );
                    return (
                      <List.Item>
                        <Tooltip title={item.rule_text} placement="right">
                          <div>{item.name}</div>
                        </Tooltip>
                        <Space>
                          {editIcon}
                          {deleteIcon}
                        </Space>
                      </List.Item>
                    );
                  }}
                />
              </div>
            </Tabs.TabPane>
          ) : null}
        </Tabs>
      </div>
    );
  }

  fillSelectedFilters = () => {
    const { updateSelectedFilters, dashboardType, filters, selectedFilterNames, defaultFilters } =
      this.props;

    if (
      [DASHBOARD_TYPE.COMPARE, DASHBOARD_TYPE.NOC, DASHBOARD_TYPE.ML].includes(dashboardType) &&
      filters.length > 0 &&
      !this.isSelectedFiltersUpdated
    ) {
      const allFilters = getFullFilters(filters, defaultFilters, dashboardType);
      // eslint-disable-next-line max-len
      const selectedFilters = selectedFilterNames.map((name) =>
        allFilters.find((filter) => filter.name === name),
      );
      updateSelectedFilters(selectedFilters.filter((filter) => filter !== undefined));
      this.isSelectedFiltersUpdated = true;
    }
  };

  onSearchSelect = (filterKey) => {
    const {
      filters,
      applyFilter,
      pushDrilldownFilter,
      dashboardType,
      selectedFilters,
      resetDrillDown,
      defaultFilters,
      dataScope,
      allDimensions,
    } = this.props;
    const selectedFilter = getFullFilters(filters, defaultFilters, dashboardType).find(
      (filter) => (filter.fid || filter.name) === filterKey,
    );

    // saved filter including event dimension can't be selected under Session data scope
    if (isAppModule && dataScope) {
      const isValidFilter = isValidSavedFilter(selectedFilter, allDimensions);
      if (!isValidFilter) {
        message.warning(FIlTER_CONTAINS_INAPPLICALBE_DIMENSION_ERROR);
        return;
      }
    }

    if (dashboardType === DASHBOARD_TYPE.COMPARE) {
      this.updateSelectedFiltersLocal(selectedFilter);
      return;
    }

    if ([DASHBOARD_TYPE.NOC, DASHBOARD_TYPE.ML].includes(dashboardType)) {
      this.updateSelectedFiltersLocal(selectedFilter);
      return;
    }

    if (selectedFilters.length) {
      const selectedFilterRule = getRulesFromFilter(get(selectedFilter, 'rule'));
      const sFilterRule = getRulesFromFilter(get(selectedFilters, '0.rule'));
      if (isDuplicateFilter(selectedFilterRule, sFilterRule)) {
        this.updateSelectedFiltersLocal(selectedFilter);
        return;
      }
    }

    applyFilter(selectedFilter);
    const tRules = getRulesFromFilter(selectedFilter.rule, true);
    const tFilter = getFilterFromRules(tRules, true);
    resetDrillDown();
    pushDrilldownFilter(selectedFilter.name || tFilter.name, tFilter.rule);
    this.onCancel();
  };

  updateSelectedFiltersLocal = (filters) => {
    const {
      compareMode,
      selectedFilters,
      router,
      maxSelectedFilters,
      dashboardType,
      updateSelectedFilters,
      maxFilters,
      nocMode,
      dataScope,
      allDimensions,
    }: any = this.props;
    const addedFilters = castArray(filters);

    if (dashboardType === DASHBOARD_TYPE.NOC) {
      if (nocMode === 'trends') {
        updateSelectedFilters(addedFilters);
      } else {
        const filteredList = xorBy(selectedFilters, addedFilters, (filter) => filter.fid);
        if (filteredList.length > maxSelectedFilters) {
          const msg = `Cannot apply more than ${maxFilters} filters`;
          message.warning(msg);
          return;
        }
        updateSelectedFilters(filteredList);
      }
      return;
    }

    if (dashboardType === DASHBOARD_TYPE.COMPARE) {
      const filteredList = xorBy(selectedFilters, addedFilters, (filter) => filter.name);

      if (filteredList.length > maxSelectedFilters) {
        const msg = `Cannot apply more than ${MAX_COMPARE_FILTER_BARS} filters`;
        message.warning(msg);

        return;
      }

      updateSelectedFilters(filteredList);
      return;
    }

    // saved filter including event dimension can't be selected under Session data scope
    if (isAppModule && dataScope) {
      const valid = isValidSavedFilter(addedFilters[0], allDimensions);
      if (!valid) {
        message.warning(INAPPLICABLE_FILTER_ERROR);
        return;
      }
    }

    /* TODO: Below code needs to be optimized for different dashboard types */
    const addedFilterNames = addedFilters.map((f) => f.name);
    const shared = selectedFilters
      .filter((f) => addedFilterNames.includes(f.name))
      .map((f) => f.name);
    const newlySelectedFilters = addedFilters
      .filter((f) => !shared.includes(f.name))
      .concat(selectedFilters.filter((f) => !shared.includes(f.name)))
      .slice(0, maxSelectedFilters);
    if (compareMode && router.location.pathname.includes(COMPARE_PATH_TEST_STR)) {
      const clonedSelectedFilters = cloneDeep(selectedFilters);
      const [currentSelectedFilter] = newlySelectedFilters;
      const existingIndex = findIndex(
        clonedSelectedFilters,
        (selectedFilter) => selectedFilter.name === currentSelectedFilter.name,
      );
      if (existingIndex !== -1) {
        clonedSelectedFilters.splice(existingIndex, 1);
        updateSelectedFilters(clonedSelectedFilters);
        return;
      }
      updateSelectedFilters(clonedSelectedFilters.concat(newlySelectedFilters));
      return;
    }
    updateSelectedFilters(newlySelectedFilters);
  };

  validateCompareFilters = () => {
    const { compareDrawer, savedFilters } = this.props;
    const validation = validateNewFilterBar(compareDrawer.filterBars, savedFilters);
    if (!validation.status) {
      message.error(validation.msg);
    }
    return validation.status;
  };

  /* Limit to apply 1 filter for now */
  applyFilters = () => {
    const {
      applyFilter,
      selectedFilters,
      compareMode,
      resetDrillDown,
      pushDrilldownFilter,
      addSavedFilters,
      modal,
      toggleModal,
      dashboardId,
      dashboardType,
      nocMode,
      applyTrendsSelectedNocFilters,
      applyMLFilter,
      activeMLFilter,
      updateSavedFiltersSelectedNocFilters,
      nocDrawerShow,
      defaultFilters,
    }: any = this.props;
    if (dashboardType === DASHBOARD_TYPE.COMPARE) {
      if (this.validateCompareFilters()) {
        /* Add saved filters to comparison */
        addSavedFilters(selectedFilters, dashboardId, true);
      }
      this.onCancel();
      return;
    }
    if (dashboardType === DASHBOARD_TYPE.NOC) {
      if (nocMode === 'trends') {
        applyTrendsSelectedNocFilters(selectedFilters);
      } else {
        const allTrafficFilter = defaultFilters.find((filter) => filter.name === ALL_TRAFFIC);
        const filterArray = [];
        // eslint-disable-next-line array-callback-return
        selectedFilters.map((eachFilter) => {
          filterArray.push(eachFilter.name === ALL_TRAFFIC ? allTrafficFilter : eachFilter);
        });
        updateSavedFiltersSelectedNocFilters(filterArray);
      }
      this.onCancel();
      return;
    }
    if (dashboardType === DASHBOARD_TYPE.ML) {
      applyMLFilter(selectedFilters[0], activeMLFilter);
      this.onCancel();
      return;
    }
    resetDrillDown();
    pushDrilldownFilter(selectedFilters[0].name, selectedFilters[0].rule);
    applyFilter(selectedFilters[0]);
    this.onCancel();
  };

  onClickRecentFilter = (filter) => () => {
    const {
      applyFilter,
      compareMode,
      resetDrillDown,
      pushDrilldownFilter,
      addSavedFilters,
      selectedFilters,
      modal,
      toggleModal,
      dashboardId,
      dashboardType,
      nocMode,
      applyTrendsSelectedNocFilters,
      applyMLFilter,
      activeMLFilter,
      updateSelectedFilters,
      nocDrawerShow,
    }: any = this.props;
    if (dashboardType === DASHBOARD_TYPE.COMPARE) {
      if (this.validateCompareFilters()) {
        /* Add recent saved filters to comparison */
        addSavedFilters([filter], dashboardId);
      }
      this.onCancel();
      return;
    }
    if (dashboardType === DASHBOARD_TYPE.NOC) {
      if (nocMode === 'trends') {
        applyTrendsSelectedNocFilters([filter]);
        this.onCancel();
      } else {
        const filteredList = selectedFilters.concat(filter);
        updateSelectedFilters(filteredList);
      }
      return;
    }
    if (dashboardType === DASHBOARD_TYPE.ML) {
      applyMLFilter(filter, activeMLFilter);
      this.onCancel();
      return;
    }
    resetDrillDown();
    if (filter.rule.length !== 0) {
      const tRules = getRulesFromFilter(filter.rule, true);
      const tFilter = getFilterFromRules(tRules, true);
      pushDrilldownFilter(filter.name || tFilter.name, tFilter.rule);
    }
    applyFilter(filter);
    this.onCancel();
  };

  onCancel = () => {
    const { changeFilterContainerVisibility } = this.props;
    changeFilterContainerVisibility(false);
  };

  confirm = () => {
    window.top.postMessage({ type: 'manage filters' }, '*');
  };

  renderFilterPills = () => {
    const { filters, selectedFilters, maxSelectedFilters, dashboardType, defaultFilters }: any =
      this.props;

    const selectedFilterIds = compact(selectedFilters.map((f) => f.fid));
    // eslint-disable-next-line max-len
    const isCompare =
      dashboardType === DASHBOARD_TYPE.COMPARE || dashboardType === DASHBOARD_TYPE.NOC;

    /* Default Filters */
    let defaultFiltersComponent;
    if (dashboardType === DASHBOARD_TYPE.NOC) {
      defaultFiltersComponent = defaultFilters.map((filter) => (
        <FilterPill
          key={filter.fid || filter.name}
          checked={
            selectedFilterIds.includes(filter.fid) ||
            selectedFilters.filter((f) => f.name === filter.name).length === 1
          }
          filter={filter}
          onChange={isCompare ? this.updateSelectedFiltersLocal : this.onClickRecentFilter(filter)}
          isCheckbox={maxSelectedFilters > 1}
          disabled={filters.length === 0}
        />
      ));
    } else {
      defaultFiltersComponent = DERIVED_FIXED_FILTER.map((filter) => (
        <FilterPill
          key={filter.fid || filter.name}
          checked={
            selectedFilterIds.includes(filter.fid) ||
            selectedFilters.filter((f) => f.name === filter.name).length === 1
          }
          filter={filter}
          onChange={isCompare ? this.updateSelectedFiltersLocal : this.onClickRecentFilter(filter)}
          isCheckbox={maxSelectedFilters > 1}
          disabled={filters.length === 0}
        />
      ));
    }

    // eslint-disable-next-line max-len
    const fixedFiltersNames =
      dashboardType === DASHBOARD_TYPE.NOC
        ? defaultFilters.map((f) => f.name)
        : FIXED_FILTERS.map((f) => f.name);

    /* Recent Filters */
    const sortedFilters = sortBy(filters, ['created']).reverse();
    const recentFilters = sortedFilters
      .filter((filter) => !fixedFiltersNames.includes(filter.name) && filter.enabled)
      .slice(0, RECENT_FILTERS_LIMIT)
      .map((filter) => (
        <FilterPill
          key={filter.fid || filter.name}
          checked={
            selectedFilterIds.includes(filter.fid) ||
            selectedFilters.filter((f) => f.name === filter.name).length === 1
          }
          filter={filter}
          onChange={isCompare ? this.updateSelectedFiltersLocal : this.onClickRecentFilter(filter)}
          isCheckbox={maxSelectedFilters > 1}
        />
      ));

    return (
      <>
        <h3 className="dib mr-3">{DEFAULT_FILTERS}</h3>
        {defaultFiltersComponent}
        {!isAppModule ? (
          <>
            <Divider style={{ margin: '.2rem 0 .5rem 0' }} />
            <h3>{RECENT_FILTERS}</h3>
            <div className={styles.savedFilters}>{recentFilters}</div>
          </>
        ) : null}
      </>
    );
  };
}

FilterContainer.defaultProps = defaultProps;
